import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    NgZone,
    OnDestroy,
    ViewChild,
    forwardRef,
    input,
    output,
    signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { TooltipIcon } from "../tooltip/tooltip";

export interface AddressValue {
    direccion: string;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    rawPlace?: unknown;
    codigoPostal: string;
}

interface AddressTooltipDetails {
    direccion: string;
    codigoPostal: string;
    placeId: string;
    latitude: string;
    longitude: string;
    localidad: string;
    provincia: string;
    pais: string;
}

type GooglePlace = google.maps.places.Place;

// interface GooglePlace {
//     id?: string;
//     formattedAddress?: string;
//     location?: GoogleMapsLatLng;
//     postalAddress?: any;
//     addressComponents?: any;
//     fetchFields: (options: { fields: string[] }) => Promise<void>;
// }

interface GooglePlacePrediction {
    toPlace: () => GooglePlace;
}

interface GooglePlaceSelectEvent extends Event {
    placePrediction?: GooglePlacePrediction;
    detail?: {
        placePrediction?: GooglePlacePrediction;
    };
}

type GooglePlaceAutocompleteElement = google.maps.places.PlaceAutocompleteElement;

type GoogleMapsWithLegacyPlacesCheck = typeof google.maps & {
    places?: {
        PlaceAutocompleteElement?: unknown;
    };
};

@Component({
    selector: 'app-address-selector',
    templateUrl: './address-selector.html',
    styleUrl: './address-selector.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AddressSelector),
            multi: true
        }
    ],
    imports: [TooltipIcon]
})
export class AddressSelector implements ControlValueAccessor, AfterViewInit, OnDestroy {
    private static googleScriptPromise: Promise<void> | null = null;
    private static nextInputId = 0;

    @ViewChild('autocompleteHost', { static: true })
    private readonly autocompleteHost?: ElementRef<HTMLDivElement>;


    readonly inputId = `address-selector-${AddressSelector.nextInputId++}`;
    readonly label = input('Address');
    readonly placeholder = input('Ingrese una dirección...');
    readonly ariaLabel = input('Búsqueda de dirección');
    readonly tooltip = input<string>('Seleccione una dirección sugerida por Google Maps.');
    readonly googleApiKey = environment.googleApiKey;
    readonly country = input<string | undefined>('ar');
    /** Optional Place Autocomplete primary types filter. Keep empty for all result types. */
    readonly types = input<string[]>([]);
    readonly readonly = input(false);

    readonly disabled = signal(false);
    readonly addressLine = signal('');
    readonly isAddressSelected = signal(false);
    readonly googleApiError = signal<string | null>(null);
    readonly selectedAddressDetails = signal<AddressTooltipDetails | null>(null);
    readonly hasSelectedAddressDetails = computed(() => this.selectedAddressDetails() !== null);

    private autocompleteElement?: GooglePlaceAutocompleteElement;
    private geocoder?: google.maps.Geocoder;
    private placeSelectHandler?: (event: Event) => void;
    private inputHandler?: () => void;
    private blurHandler?: () => void;
    private keyDownHandler?: (event: Event) => void;
    private onChange: (value: AddressValue | null) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(private readonly ngZone: NgZone) {
        effect(() => {
            this.syncInputReadonlyState();
        });
    }

    async ngAfterViewInit(): Promise<void> {
        try {
            await this.initializeAutocomplete();
        } catch (error: unknown) {
            this.setGoogleApiError(error, 'Google Places failed to initialize.');
        }
        this.syncInputElementValue();
    }

    ngOnDestroy(): void {
        this.removeElementListeners();
    }

    writeValue(value: AddressValue | string | null): void {
        if (!value) {
            this.addressLine.set('');
            this.isAddressSelected.set(false);
            this.selectedAddressDetails.set(null);
            this.syncInputElementValue();
            return;
        }

        if (typeof value === 'string') {
            this.addressLine.set(value);
            this.isAddressSelected.set(false);
            this.selectedAddressDetails.set(null);
            this.syncInputElementValue();
            return;
        }

        this.addressLine.set(value.direccion ?? '');
        this.isAddressSelected.set(!!value.placeId);
        this.selectedAddressDetails.set({
            direccion: value.direccion ?? '',
            codigoPostal: value.codigoPostal ?? '',
            placeId: value.placeId ?? '',
            latitude: value.latitude != null ? `${value.latitude}` : '',
            longitude: value.longitude != null ? `${value.longitude}` : '',
            localidad: '',
            provincia: '',
            pais: ''
        });
        this.syncInputElementValue();
    }

    registerOnChange(fn: (value: AddressValue | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
        this.syncInputDisabledState();
    }

    onManualInput(event: Event): void {
        const target = event.target as HTMLInputElement | null;
        const text = target?.value ?? '';

        this.addressLine.set(text);
        this.isAddressSelected.set(false);
        this.selectedAddressDetails.set(null);
        // if (!text.trim()) {
        //     this.onChange(null);
        //     return;
        // }

        // let value: AddressValue = {
        //     direccion: text,
        // };
        if (!text.trim()) {
            this.onChange(null);
        }
    }

    onInputBlur(): void {
        this.onTouched();
    }

    onEnter(event: Event): void {
        const currentValue = this.autocompleteElement?.value ?? '';
        const canSubmit = this.isAddressSelected() && currentValue.trim() === this.addressLine().trim();

        if (!canSubmit) {
            event.preventDefault();
        }
    }

    focus(): void {
        this.autocompleteElement?.focus();
    }


    private async initializeAutocomplete(): Promise<void> {

        this.googleApiError.set(null);
        await this.loadGooglePlacesScript(this.googleApiKey);

        const hostEl = this.autocompleteHost?.nativeElement;
        const maps = globalThis.google?.maps;
        if (!hostEl || !maps) {
            this.googleApiError.set('Google Places library is unavailable.');
            return;
        }

        if (maps.Geocoder) {
            this.geocoder = new maps.Geocoder();
        }

        const PlaceAutocompleteCtor = await this.getPlaceAutocompleteConstructor(maps);
        if (!PlaceAutocompleteCtor) {
            this.googleApiError.set('PlaceAutocompleteElement is unavailable. Enable Places API (New) and use Maps JS weekly version.');
            return;
        }

        const placeAutocomplete = new PlaceAutocompleteCtor({});
        placeAutocomplete.setAttribute('id', this.inputId);
        placeAutocomplete.setAttribute('aria-label', this.ariaLabel());
        placeAutocomplete.classList = 'address-autocomplete';
        placeAutocomplete.style.width = '100%';
        placeAutocomplete.placeholder = this.placeholder();
        placeAutocomplete.disabled = this.disabled();
        placeAutocomplete.readonly = this.readonly();
        placeAutocomplete.value = this.addressLine();

        // const style = placeAutocomplete.style;
        // style.setProperty('background-color', 'red');
        // style.width = '100%';


        const country = this.country();
        if (country) {
            placeAutocomplete.includedRegionCodes = [country];
        }

        const types = this.types();
        if (types.length > 0) {
            placeAutocomplete.includedPrimaryTypes = types;
        }

        this.placeSelectHandler = (event: Event) => {
            const placeSelectEvent = event as GooglePlaceSelectEvent;
            const placePrediction = placeSelectEvent.placePrediction ?? placeSelectEvent.detail?.placePrediction;
            if (!placePrediction) {
                return;
            }

            void this.ngZone.run(async () => {
                try {
                    const place = placePrediction.toPlace();
                    await place.fetchFields({ fields: ['formattedAddress', 'location', 'postalAddress', 'addressComponents'] });

                    var line = this.autocompleteElement?.value ?? '';
                    line = this.removeCountryFromAddress(line);
                    const cp = await this.applySelectedPlace(place);
                    this.googleApiError.set(null);

                    const location = place.location;
                    this.addressSelected.emit({
                        direccion: line,
                        placeId: place.id,
                        latitude: location?.lat(),
                        longitude: location?.lng(),
                        rawPlace: place,
                        codigoPostal: cp,
                    });
                } catch (error: unknown) {
                    this.setGoogleApiError(error, 'Failed to read the selected place details.');
                }
            });
        };

        this.inputHandler = () => {
            this.ngZone.run(() => {
                const text = this.autocompleteElement?.value ?? '';
                this.onManualInput({ target: { value: text } } as unknown as Event);
            });
        };

        this.keyDownHandler = (event: Event) => {
            const keyboardEvent = event as KeyboardEvent;
            if (keyboardEvent.key !== 'Enter') {
                return;
            }

            this.ngZone.run(() => this.onEnter(event));
        };

        this.blurHandler = () => {
            this.ngZone.run(() => this.onInputBlur());
        };

        placeAutocomplete.addEventListener('gmp-select', this.placeSelectHandler);
        placeAutocomplete.addEventListener('gmp-placeselect', this.placeSelectHandler);
        placeAutocomplete.addEventListener('input', this.inputHandler);
        placeAutocomplete.addEventListener('keydown', this.keyDownHandler);
        placeAutocomplete.addEventListener('focusout', this.blurHandler);

        hostEl.replaceChildren(placeAutocomplete);
        this.autocompleteElement = placeAutocomplete;
    }

    addressSelected = output<AddressValue>();
    private async applySelectedPlace(place: GooglePlace): Promise<string> {
        const line = this.autocompleteElement?.value ?? place?.formattedAddress ?? '';
        this.addressLine.set(line);
        this.isAddressSelected.set(!!place?.id && !!line.trim());
        this.syncInputElementValue();

        if (!line.trim()) {
            this.onChange(null);
            return '';
        }

        const location = place?.location;
        var cp = place.addressComponents?.find(x => x.types?.includes('postal_code'))?.longText ?? '';
        if (!cp.trim()) {
            cp = await this.reverseGeocodePostalCode(location);
        }
        cp = this.removeFirstNonNumericChar(cp);
        let value: AddressValue = {
            //direccion: place?.postalAddress?.addressLines.join(",") + ", " + place?.postalAddress?.locality + ", " + place?.postalAddress?.administrativeArea,
            direccion: this.removeCountryFromAddress(line),
            placeId: place?.id,
            latitude: location?.lat(),
            longitude: location?.lng(),
            codigoPostal: cp,
        };

        const localidad = this.getAddressComponentLongText(place, 'locality');
        const provincia = this.getAddressComponentLongText(place, 'administrative_area_level_1');
        const pais = this.getAddressComponentLongText(place, 'country');
        this.selectedAddressDetails.set({
            direccion: value.direccion,
            codigoPostal: value.codigoPostal,
            placeId: value.placeId ?? '',
            latitude: value.latitude != null ? `${value.latitude}` : '',
            longitude: value.longitude != null ? `${value.longitude}` : '',
            localidad,
            provincia,
            pais
        });

        this.onChange(value);
        return cp;
    }

    private getAddressComponentLongText(place: GooglePlace, type: string): string {
        return place.addressComponents?.find(x => x.types?.includes(type))?.longText ?? '';
    }

    private reverseGeocodePostalCode(location?: google.maps.LatLng | null): Promise<string> {
        if (!location || !this.geocoder) {
            return Promise.resolve('');
        }

        return new Promise<string>((resolve) => {
            this.geocoder?.geocode({ location }, (results, status) => {
                if (status !== 'OK' || !results?.length) {
                    resolve('');
                    return;
                }

                const postalCode = results[0].address_components?.find((component) => component.types?.includes('postal_code'))?.long_name ?? '';
                resolve(postalCode);
            });
        });
    }
    removeFirstNonNumericChar(cp: string): string {
        return cp.replace(/^[^\d]+/, '');
    }

    private removeCountryFromAddress(address: string): string {
        const index = address.lastIndexOf(',');
        if (index === -1) {
            return address;
        }
        return address.substring(0, index).trim();
    }

    private syncInputElementValue(): void {
        if (this.autocompleteElement) {
            this.autocompleteElement.value = this.addressLine();
        }
    }

    private syncInputDisabledState(): void {
        if (this.autocompleteElement) {
            this.autocompleteElement.disabled = this.disabled();
        }
    }

    private syncInputReadonlyState(): void {
        if (this.autocompleteElement) {
            this.autocompleteElement.readonly = this.readonly();
        }
    }

    private removeElementListeners(): void {
        if (!this.autocompleteElement) {
            return;
        }

        if (this.placeSelectHandler) {
            this.autocompleteElement.removeEventListener('gmp-select', this.placeSelectHandler);
            this.autocompleteElement.removeEventListener('gmp-placeselect', this.placeSelectHandler);
        }
        if (this.inputHandler) {
            this.autocompleteElement.removeEventListener('input', this.inputHandler);
        }
        if (this.keyDownHandler) {
            this.autocompleteElement.removeEventListener('keydown', this.keyDownHandler);
        }
        if (this.blurHandler) {
            this.autocompleteElement.removeEventListener('focusout', this.blurHandler);
        }
    }

    private loadGooglePlacesScript(apiKey: string): Promise<void> {
        const maps = globalThis.google?.maps as GoogleMapsWithLegacyPlacesCheck | undefined;
        if (maps?.importLibrary || maps?.places?.PlaceAutocompleteElement) {
            return Promise.resolve();
        }

        if (AddressSelector.googleScriptPromise) {
            return AddressSelector.googleScriptPromise;
        }

        AddressSelector.googleScriptPromise = new Promise<void>((resolve, reject) => {
            const scriptId = 'google-places-script';
            const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
            let settled = false;

            const resolveIfPlacesReady = (): void => {
                const currentMaps = globalThis.google?.maps as GoogleMapsWithLegacyPlacesCheck | undefined;
                if (currentMaps?.importLibrary || currentMaps?.places?.PlaceAutocompleteElement) {
                    settled = true;
                    resolve();
                    return;
                }

                settled = true;
                reject(new Error('Google Maps loaded but Place Autocomplete is unavailable.'));
            };

            const waitForPlacesReady = (): void => {
                const maxAttempts = 40;
                let attempts = 0;

                const timer = window.setInterval(() => {
                    if (settled) {
                        window.clearInterval(timer);
                        return;
                    }

                    const currentMaps = globalThis.google?.maps as GoogleMapsWithLegacyPlacesCheck | undefined;
                    if (currentMaps?.importLibrary || currentMaps?.places?.PlaceAutocompleteElement) {
                        window.clearInterval(timer);
                        settled = true;
                        resolve();
                        return;
                    }

                    attempts += 1;
                    if (attempts >= maxAttempts) {
                        window.clearInterval(timer);
                        settled = true;
                        reject(new Error('Google Places initialization timed out.'));
                    }
                }, 100);
            };

            if (existingScript) {
                // If another part of the app already injected the script, load may have already fired.
                if (existingScript.dataset['loaded'] === 'true') {
                    resolveIfPlacesReady();
                    return;
                }

                // If the script tag exists but load already happened elsewhere, start readiness checks now.
                const existingScriptReadyState = (existingScript as unknown as { readyState?: string }).readyState;
                if (existingScriptReadyState === 'complete') {
                    waitForPlacesReady();
                }

                existingScript.addEventListener('load', () => {
                    existingScript.dataset['loaded'] = 'true';
                    waitForPlacesReady();
                }, { once: true });
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Places script.')), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&v=weekly&language=es`;
            script.async = true;
            script.defer = true;
            script.addEventListener('load', () => {
                script.dataset['loaded'] = 'true';
                waitForPlacesReady();
            }, { once: true });
            script.addEventListener('error', () => reject(new Error('Failed to load Google Places script.')), { once: true });

            document.head.appendChild(script);
        }).catch(error => {
            AddressSelector.googleScriptPromise = null;
            throw error;
        });

        return AddressSelector.googleScriptPromise;
    }

    private async getPlaceAutocompleteConstructor(maps: typeof google.maps): Promise<typeof google.maps.places.PlaceAutocompleteElement | undefined> {
        if (maps.importLibrary) {
            const placesLibrary = await maps.importLibrary('places');
            if (placesLibrary.PlaceAutocompleteElement) {
                return placesLibrary.PlaceAutocompleteElement;
            }
        }

        return undefined;
    }

    private setGoogleApiError(error: unknown, fallbackMessage: string): void {
        if (error instanceof Error && error.message.trim()) {
            this.googleApiError.set(error.message);
            return;
        }

        this.googleApiError.set(fallbackMessage);
    }
}